import { useEffect, useState } from 'react';
import {
  getShorteners,
  createShortener,
  updateShortener,
  type Shortener,
} from '../api/marketing';
import { ApiError } from '../api/client';
import { Button } from '../components/arc/button/button';
import { ArcPanel } from '../components/arc/primitives/arc-primitives';
import './domain-settings-arc.css';

interface FormState {
  hostname: string;
  server_ip: string;
  channels: string;
  ssl_enabled: boolean;
  description: string;
}
const EMPTY: FormState = { hostname: '', server_ip: '', channels: 'sms', ssl_enabled: true, description: '' };

export function ShortenerSettings() {
  const [items, setItems] = useState<Shortener[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);

  function reload() {
    getShorteners()
      .then((d) => { setItems(d); setError(null); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }
  useEffect(reload, []);

  function startAdd() { setForm(EMPTY); setEditId(null); setAdding(true); }
  function startEdit(s: Shortener) {
    setForm({ hostname: s.hostname, server_ip: s.serverIp ?? '', channels: s.channels, ssl_enabled: s.sslEnabled, description: s.description ?? '' });
    setAdding(false);
    setEditId(s.id);
  }
  function cancel() { setAdding(false); setEditId(null); setForm(EMPTY); }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (adding) {
        await createShortener({
          hostname: form.hostname.trim(),
          server_ip: form.server_ip.trim() || undefined,
          channels: form.channels.trim() || undefined,
          ssl_enabled: form.ssl_enabled,
          description: form.description.trim() || undefined,
        });
      } else if (editId != null) {
        await updateShortener(editId, {
          server_ip: form.server_ip.trim(),
          channels: form.channels.trim(),
          ssl_enabled: form.ssl_enabled,
          description: form.description.trim(),
        });
      }
      cancel();
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(s: Shortener) {
    try {
      await updateShortener(s.id, { active: !s.active });
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Změna stavu selhala');
    }
  }

  const editing = adding || editId != null;

  return (
    <ArcPanel className="p1-domain-settings p1-arc-scope">
      <div className="p1-domain-settings-head">
        <h2>Shortener domény</h2>
        {!editing && (
          <Button type="button" size="sm" onClick={startAdd}>Přidat doménu</Button>
        )}
      </div>
      <div className="p1-domain-settings-body">
        {error && <div className="alert alert-danger py-2">{error}</div>}

        {editing && (
          <div className="p1-domain-settings-form">
            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label small mb-1">Doména (hostname)</label>
                <input className="form-control form-control-sm" placeholder="rondo.to" value={form.hostname}
                  disabled={!adding}
                  onChange={(e) => setForm({ ...form, hostname: e.target.value })} />
                {!adding && <div className="form-text">Hostname je read-only (změna domény = nový záznam).</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label small mb-1">Server IP (DNS A target)</label>
                <input className="form-control form-control-sm" placeholder="62.4.5.163" value={form.server_ip}
                  onChange={(e) => setForm({ ...form, server_ip: e.target.value })} />
              </div>
              <div className="col-md-6">
                <label className="form-label small mb-1">Kanály (CSV)</label>
                <input className="form-control form-control-sm" placeholder="sms / sms,notif" value={form.channels}
                  onChange={(e) => setForm({ ...form, channels: e.target.value })} />
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="sh-ssl" checked={form.ssl_enabled}
                    onChange={(e) => setForm({ ...form, ssl_enabled: e.target.checked })} />
                  <label className="form-check-label small" htmlFor="sh-ssl">SSL (Let's Encrypt)</label>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label small mb-1">Popis</label>
                <input className="form-control form-control-sm" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <div className="p1-domain-settings-actions">
              <Button type="button" size="sm" disabled={saving || (adding && !form.hostname.trim())} onClick={() => void save()}>
                {saving ? 'Ukládám…' : 'Uložit'}
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={cancel} disabled={saving}>Zrušit</Button>
            </div>
          </div>
        )}

        {items == null ? (
          <div className="text-muted py-4"><span className="spinner-border spinner-border-sm me-2" />Načítám…</div>
        ) : items.length === 0 ? (
          <div className="text-muted py-4 text-center">Zatím žádná shortener doména.</div>
        ) : (
          <div className="p1-domain-settings-list">
            {items.map((s) => (
              <div key={s.id} className="p1-domain-settings-row">
                <div className="p1-domain-settings-row-copy">
                  <div className="p1-domain-settings-row-title">
                    <strong>{s.hostname}</strong>
                    {s.active
                      ? <span className="p1-domain-settings-badge" data-state="active">Aktivní</span>
                      : <span className="p1-domain-settings-badge">Neaktivní</span>}
                    {s.sslEnabled && <span className="p1-domain-settings-badge">SSL</span>}
                  </div>
                  <div className="p1-domain-settings-row-meta">
                    <span>{s.channels || 'Bez kanálu'}</span>
                    <span>{[s.serverIp, s.description].filter(Boolean).join(' · ') || 'Bez dalšího popisu'}</span>
                  </div>
                </div>
                <div className="p1-domain-settings-row-actions">
                  <Button type="button" variant="ghost" size="sm" onClick={() => void toggleActive(s)}>{s.active ? 'Deaktivovat' : 'Aktivovat'}</Button>
                  <Button type="button" variant="secondary" size="sm" onClick={() => startEdit(s)}>Upravit</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ArcPanel>
  );
}
